<template>
  <div
    v-apply-post-links-styles
    data-test="graphics-socialSettings"
  >
    <SidebarSettingsGroup
      title="Preview Social Data"
      collapsible
      :element-type="element.type"
      data-test="graphics-socialSettings-preview"
    >
      <div class="settings-row sidebar-form-group">
        <div class="settings-col-12">
          <label
            for="feedSelected"
            class="sidebar-form-label"
          >Select a Board</label>
        </div>
        <div class="settings-col-12">
          <select
            id="feedSelected"
            v-model="feedSelected"
            data-test="graphics-selectTagboard"
            data-entity="graphics-selectTagboard"
            @change="fetchThenUpdate"
          >
            <option
              v-for="tagboard in tagboards"
              :key="tagboard._id"
              :value="tagboard._id"
              :data-test="`graphics-selectTagboard-${tagboard._id}`"
            >
              {{ tagboard.name }}
            </option>
          </select>
        </div>
        <div class="settings-col-12">
          <label
            class="checkbox display-inline-block"
            for="mutePreview"
          >Mute Preview
            <input
              id="mutePreview"
              v-model="previewMuted"
              type="checkbox"
              value="mutePreview"
              data-test="graphics-muteSocialPreview"
              data-entity="graphics-muteSocialPreview"
            >
            <div class="checkmark">
              <span class="checkmark-tick fa-solid fa-check" />
            </div>
          </label>
        </div>
      </div>
      <div
        v-if="postsLength > 1"
        class="settings-row sidebar-form-group"
      >
        <div class="settings-col-12">
          <h3 class="sidebar-form-label margin-top-sm margin-bottom-sm">
            View More Posts
          </h3>
        </div>
        <div class="settings-col-12">
          <div class="tagboard-post-iterator">
            <button
              type="button"
              class="btn btn-sm btn-style-b tagboard-post-iterator__next"
              data-test="graphics-previewNextSocialPost"
              data-entity="graphics-previewNextSocialPost"
              :disabled="nextPostDisabled"
              @click="updateCurrentPost(1)"
            >
              <span class="fas fa-forward" /> Next
            </button>
            <button
              type="button"
              class="btn btn-sm btn-style-b tagboard-post-iterator__prev"
              data-test="graphics-previewPrevSocialPost"
              data-entity="graphics-previewPrevSocialPost"
              :disabled="prevPostDisabled"
              @click="updateCurrentPost(-1)"
            >
              <span class="fas fa-backward" /> Prev
            </button>
          </div>
        </div>
      </div>
    </SidebarSettingsGroup>
    <SidebarSettingsGroup
      class="settings-social-hashtag-mentions"
      title="Hashtag, Mentions, & Link Styling"
      collapsible
      :element-type="element.type"
      :tooltip="true"
      tooltip-label="For supported networks only."
      tooltip-pos="down-left"
      data-test="graphics-socialSettings-hashtagMentions"
    >
      <div class="settings-row-xs no-tooltip auto-size settings-row sidebar-form-group">
        <div class="settings-col-12">
          <label
            class="checkbox header-checkbox"
          >
            Sync All Colors
            <input
              v-model="useGlobalLinkColor"
              type="checkbox"
              data-test="graphics-socialSettings-toggleSyncAllColors"
              data-entity="graphics-socialSettings-toggleSyncAllColors"
            >
            <div
              aria-label="Color All Links"
              class="checkmark"
            >
              <span class="checkmark-tick fa-solid fa-check" />
            </div>
          </label>
          <div
            v-if="useGlobalLinkColor"
            class="settings-row settings-row-xs"
            data-test="graphics-socialSettings-globalLinkColor"
          >
            <div class="settings-col-12">
              <div class="colorpicker">
                <colorpicker
                  v-model="globalLinkColor"
                  :is-mapped="false"
                />
              </div>
              <label class="sidebar-input-sub-label">
                Color
              </label>
            </div>
          </div>
        </div>
        <div class="settings-col-12">
          <div
            v-show="!useGlobalLinkColor"
            class="colorpicker"
            data-test="graphics-socialSettings-hashtagLinkColor"
          >
            <label class="sidebar-form-label">
              Hashtags </label>
            <colorpicker
              v-model="hashtagLinkColor"
              :is-mapped="false"
            />
            <label class="sidebar-input-sub-label">
              Color
            </label>
          </div>
        </div>
        <div class="settings-col-12">
          <div
            v-show="!useGlobalLinkColor"
            class="colorpicker"
            data-test="graphics-socialSettings-mentionLinkColor"
          >
            <label class="sidebar-form-label">
              @ Mentions </label>
            <colorpicker
              v-model="mentionLinkColor"
              :is-mapped="false"
            />
            <label class="sidebar-input-sub-label">
              Color
            </label>
          </div>
        </div>
        <div class="settings-col-12">
          <div
            v-show="!useGlobalLinkColor"
            class="colorpicker"
            data-test="graphics-socialSettings-cashtagLinkColor"
          >
            <label class="sidebar-form-label">
              Cashtags </label>
            <colorpicker
              v-model="cashtagLinkColor"
              :is-mapped="false"
            />
            <label class="sidebar-input-sub-label">
              Color
            </label>
          </div>
        </div>
        <div class="settings-col-12">
          <label
            class="checkbox header-checkbox"
          >
            Show URLs
            <input
              v-model="showOtherLinks"
              type="checkbox"
              data-test="graphics-socialSettings-toggleShowOtherLinks"
              data-entity="graphics-socialSettings-toggleShowOtherLinks"
            >
            <div
              aria-label="Hide or Show Other Links"
              class="checkmark"
            >
              <span class="checkmark-tick fa-solid fa-check" />
            </div>
          </label>
          <div
            v-if="showOtherLinks && !useGlobalLinkColor"
            class="settings-row settings-row-xs"
            data-test="graphics-socialSettings-quoteLinkColor"
          >
            <div class="settings-col-12">
              <div class="colorpicker">
                <colorpicker
                  v-model="quoteLinkColor"
                  :is-mapped="false"
                  data-test="graphics-socialSettings-quoteLinkColorPicker"
                />
              </div>
              <label class="sidebar-input-sub-label">
                Color
              </label>
            </div>
          </div>
        </div>
        <div class="settings-col-12">
          <label
            class="checkbox header-checkbox"
          >
            Remove Spaces
            <input
              v-model="removeSpaces"
              type="checkbox"
              data-test="graphics-socialSettings-toggleRemoveSpaces"
              data-entity="graphics-socialSettings-toggleRemoveSpaces"
            >
            <div
              aria-label="Remove Spaces"
              class="checkmark"
            >
              <span class="checkmark-tick fa-solid fa-check" />
            </div>
          </label>
        </div>
      </div>
    </SidebarSettingsGroup>
    <SidebarSettingsGroup
      title="Production Settings"
      collapsible
      :element-type="element.type"
      data-test="graphics-socialSettings-production"
    >
      <cycle-time-settings
        v-model:cycle-time="cycleTime"
        action="updateSocialPostCycleSpeed"
      />
      <hr class="sidebar-divider margin-top-md margin-bottom-md">
      <animation-settings
        :element="element"
      />
    </SidebarSettingsGroup>
  </div>
</template>

<script>
import {
  mapActions, mapMutations, mapState, mapGetters,
} from 'vuex';
import SettingsMixins from '../../../../mixins/settings-mixins';
import AnimationSettings from '../../Animation.vue';
import CycleTimeSettings from '../../CycleTime.vue';

export default {
  name: 'SocialSettings',
  components: {
    AnimationSettings,
    CycleTimeSettings,
  },
  mixins: [SettingsMixins],
  props: {
    element: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      feedData: [],
      globalLinkColor: '#2bacca',
    };
  },
  computed: {
    ...mapGetters('graphicsTemplate', ['getElementById', 'getElementGroupMap']),
    ...mapState({
      tagboards: (state) => state.user.tagboards,
      // namespaced - arrow functions don't work here because of the scope of this
      tagboardId(state) {
        return state[this.id].tagboardId;
      },
      mutePreview(state) {
        return state[this.id].mutePreview;
      },
      postsLength(state) {
        return state[this.id].posts.length;
      },
      displayedPostIndex(state) {
        return state[this.id].displayedPostIndex;
      },
      socialTemplate(state) {
        return state[this.id].socialTemplate;
      },
      map(state) {
        return state[this.id].map;
      },
      // same with mapGetters
      getPostByIndex(state, getters) {
        return getters[`${[this.id]}/getPostByIndex`];
      },
    }),
    id() {
      return this.element.id;
    },
    currentPost() {
      return this.getPostByIndex(this.displayedPostIndex);
    },
    childElements() {
      return this.getElementGroupMap[this.id] || [];
    },
    childElementsReversed() {
      return [...this.childElements].reverse();
    },
    feedSelected: {
      get() {
        return this.tagboardId;
      },
      set(value) {
        this.updateTagboardId(value);
      },
    },
    previewMuted: {
      get() {
        return this.mutePreview;
      },
      set(v) {
        this.updateMutePreview(v);
      },
    },
    cycleTime: {
      get() {
        return this.element.cycleSpeed;
      },
      set(v) {
        this.updateOneElementValue('cycleSpeed', v);
      },
    },
    useGlobalLinkColor: {
      get() {
        return this.element.postSettings.globalLinkColor ?? true;
      },
      set(v) {
        this.updateOneElementValue('postSettings.globalLinkColor', v);
      },
    },
    showOtherLinks: {
      get() {
        return this.element.postSettings.showOtherLinks ?? false;
      },
      set(v) {
        this.updateOneElementValue('postSettings.showOtherLinks', v);
      },
    },
    quoteLinkColor: {
      get() {
        return this.element.postSettings.linkStyles.link.color;
      },
      set(v) {
        this.updateOneElementValue('postSettings.linkStyles.link.color', v);
      },
    },
    mentionLinkColor: {
      get() {
        return this.element.postSettings.linkStyles.mention.color;
      },
      set(v) {
        this.updateOneElementValue('postSettings.linkStyles.mention.color', v);
      },
    },
    hashtagLinkColor: {
      get() {
        return this.element.postSettings.linkStyles.hashtag.color;
      },
      set(v) {
        this.updateOneElementValue('postSettings.linkStyles.hashtag.color', v);
      },
    },
    cashtagLinkColor: {
      get() {
        return this.element.postSettings.linkStyles.cashtag.color;
      },
      set(v) {
        this.updateOneElementValue('postSettings.linkStyles.cashtag.color', v);
      },
    },
    removeSpaces: {
      get() {
        return this.element.postSettings.removeSpaces ?? false;
      },
      set(v) {
        this.updateOneElementValue('postSettings.removeSpaces', v);
      },
    },
    prevPostDisabled() {
      return this.displayedPostIndex <= 0;
    },
    nextPostDisabled() {
      return this.displayedPostIndex === this.postsLength.length - 1;
    },
  },
  watch: {
    globalLinkColor() {
      if (this.useGlobalLinkColor) {
        this.updateAllLinkColors();
      }
    },
    useGlobalLinkColor(newVal) {
      if (newVal) {
        this.updateAllLinkColors();
      }
    },
  },
  created() {
    this.$store.dispatch('getUserTagboards');

    if (this.useGlobalLinkColor) {
      this.globalLinkColor = this.element.postSettings.linkStyles.link.color;
    }
  },
  methods: {
    ...mapActions('graphicsTemplate', ['setOneElementValueByProp']),
    // map Actions for dynamic store module namespaced for element id
    // must map this way because of dynamic namespace that comes from prop value
    ...mapActions({
      updateTagboardId(dispatch, payload) {
        return dispatch(`${this.id}/updateTagboardId`, payload);
      },
      updateMutePreview(dispatch, payload) {
        return dispatch(`${this.id}/updateMutePreview`, payload);
      },
      fetchFeaturedPosts(dispatch, payload) {
        return dispatch(`${this.id}/fetchFeaturedPosts`, payload);
      },
    }),
    // map Mutations for dynamic store module namespaced for element id
    // must map this way because of dynamic namespace that comes from prop value
    ...mapMutations({
      updateDisplayedPostIndex(commit, payload) {
        return commit(`${this.id}/updateDisplayedPostIndex`, payload);
      },
    }),

    fetchThenUpdate() {
      this.fetchFeaturedPosts();
    },
    updateCurrentPost(increment) {
      const postsCount = this.postsLength;
      const nextPost = increment + this.displayedPostIndex;
      const invalidPostIndex = nextPost < 0 || nextPost >= postsCount;
      if (!invalidPostIndex) {
        this.updateDisplayedPostIndex(nextPost);
      }
    },
    updateAllLinkColors() {
      this.quoteLinkColor = this.globalLinkColor;
      this.mentionLinkColor = this.globalLinkColor;
      this.hashtagLinkColor = this.globalLinkColor;
      this.cashtagLinkColor = this.globalLinkColor;
    },
  },
};
</script>

<style lang="scss">
.settings-social-hashtag-mentions {
  /* all the positions for ballon get cut off, so adding specific styles */
  [data-balloon]::after {
    top: 16px;
    left: -45px;
  }
}
</style>
